import {
  queryOptions,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';

import { UpdateNoteForm } from '@components/Notes/UpdateForm';
import { PageTitle } from '@components/PageTitle';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { updateProviderCounts } from '@helpers/queryCache/updateTabCounts.helper';
import { noteService } from '@services/notes/service';

function noteQueryOptions(id: string) {
  return queryOptions({
    queryFn: () => noteService.getById(id),
    queryKey: queryKeys.notes.detail(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/providers/$id/notes/$noteId/update',
)({
  component: ProviderNoteUpdate,
  loader: ({ context, params }) => {
    const options = noteQueryOptions(params.noteId);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMinMs: 600,
  pendingMs: 300,
});

function ProviderNoteUpdate() {
  const { id: providerId, noteId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery(noteQueryOptions(noteId));

  return (
    <>
      <PageTitle
        hasBackButton
        params={{ id: providerId }}
        title="Update note"
        to="/providers/$id"
      />
      <UpdateNoteForm
        note={data.note}
        onCancel={() =>
          navigate({ params: { id: providerId }, to: '/providers/$id' })
        }
        onDelete={() =>
          updateProviderCounts({
            delta: -1,
            field: 'notes',
            providerId,
            queryClient,
          })
        }
        onSuccess={() =>
          navigate({
            params: { id: providerId },
            replace: true,
            to: '/providers/$id',
          })
        }
        queryKeyToInvalidate={queryKeys.providers.notes(providerId)}
      />
    </>
  );
}
